import React, { memo, useCallback, useMemo, useState } from 'react'
import { Modal, Typography } from '@mui/material'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { colors } from 'src/theme'
import { useDispatch } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { SubmitFundingMethodRequest } from 'src/constants/actionTypes'
import { handleApiError } from 'src/utils/errorHandlers'
import { FundingType } from 'src/constants/payment/enums'
import { FundingInvoice } from 'src/interfaces/payment'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  isOpen?: boolean
  onClose(): void
  onInstant(): void
  onBankTransfer(): void
  handleUpdateCurrentInvoice(invoice: FundingInvoice): void
}

const ChooseFundMethodModalComponent = ({
  isOpen,
  onClose,
  onInstant,
  onBankTransfer,
  handleUpdateCurrentInvoice,
}: Props) => {
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const container = useMemo(() => document.getElementById('root'), [])

  const handleInstant = useCallback(async () => {
    setIsLoading(true)

    try {
      const currentInvoice: FundingInvoice = await createAsyncAction(
        dispatch,
        SubmitFundingMethodRequest.request({ type: FundingType.Loan }),
      )

      handleUpdateCurrentInvoice(currentInvoice)

      onInstant()
      onClose()
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [onInstant, onClose, handleUpdateCurrentInvoice])

  const handleBankTransfer = useCallback(async () => {
    setIsLoading(true)

    try {
      const currentInvoice: FundingInvoice = await createAsyncAction(
        dispatch,
        SubmitFundingMethodRequest.request({ type: FundingType.Wire }),
      )

      handleUpdateCurrentInvoice(currentInvoice)

      onBankTransfer()
      onClose()
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [onBankTransfer, onClose, handleUpdateCurrentInvoice])

  return (
    <Modal
      container={container}
      open={Boolean(isOpen)}
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4`}
    >
      <div
        data-testid="payments-main-fund-choose-method-modal"
        css={[tw`bg-white rounded-lg overflow-hidden`]}
      >
        <ModalHeader
          title="Fund settlements"
          onClose={onClose}
          closeButtonDataTestId="payments-main-fund-choose-method-modal-close-btn"
        />
        <div css={tw`p-4 flex flex-col`}>
          <Typography variant="h4" css={tw`text-center`}>
            Choose how you want to fund settlements
          </Typography>
          <Typography
            variant="body2"
            color={colors.SHADES_GREY_400}
            css={tw`mt-1 mb-6 text-center w-[300px] self-center`}
          >
            Pay contractors with our immediate funding option, or transfer the funds to us on your
            schedule.
          </Typography>
          <div css={tw`flex flex-row gap-2`}>
            <div
              css={tw`p-4 rounded-2xl w-[268px] border border-solid border-[#EDEDED] flex flex-col`}
            >
              <GSIcon iconName="gs_zap" css={tw`mb-2 w-5 h-5 self-center`} />
              <Typography variant="h4" css={tw`text-center`}>
                Instant
              </Typography>
              <Typography
                variant="body2"
                color={colors.SHADES_GREY_400}
                css={tw`w-[178px] text-center self-center`}
              >
                Don't wait and fund settlements within a few minutes
              </Typography>
              <Typography
                variant="body2"
                color={colors.SHADES_GREY_400}
                css={tw`mt-4 mb-6 text-center`}
              >
                Using our unique instant funding service, you can pay contractors now and repay us
                later.
              </Typography>
              <a
                css={tw`no-underline self-center`}
                href="https://www.gigsafe.com/terms"
                target="_blank"
              >
                <Typography
                  css={tw`cursor-pointer inline-block hover:text-[rgba(95, 0, 191, 1)]`}
                  variant="subtitle1"
                  color={colors.PRIMARY_PURPLE_500}
                >
                  Terms & Conditions
                </Typography>
              </a>
              <Button
                size="small"
                className={tw`w-full mt-3`}
                color="light"
                onClick={handleInstant}
                disabled={isLoading}
                data-testid="payments-main-fund-select-instant-btn"
              >
                Select Instant
              </Button>
            </div>
            <div
              css={tw`p-4 rounded-2xl w-[268px] border border-solid border-[#EDEDED] flex flex-col`}
            >
              <GSIcon iconName="gs_bank" css={tw`mb-2 w-5 h-5 self-center`} />
              <Typography variant="h4" css={tw`text-center`}>
                Bank Transfer
              </Typography>
              <Typography
                variant="body2"
                color={colors.SHADES_GREY_400}
                css={tw`w-[178px] text-center self-center`}
              >
                Have time to wait? Send us an ACH or Wire
              </Typography>
              <Typography
                variant="body2"
                color={colors.SHADES_GREY_400}
                css={tw`mt-4 mb-6 text-center`}
              >
                Simply send us the total amount required and we will process the settlements as soon
                as we receive funds.
              </Typography>
              <Button
                size="small"
                className={tw`w-full mt-auto`}
                color="light"
                onClick={handleBankTransfer}
                disabled={isLoading}
                data-testid="payments-main-fund-select-bank-transfer-btn"
              >
                Select Bank Transfer
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export const ChooseFundMethodModal = memo(ChooseFundMethodModalComponent)
